import DoneIcon from '@mui/icons-material/Done';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SnoozeOutlinedIcon from '@mui/icons-material/SnoozeOutlined';
import { Box, Grid, Paper, Stack, IconButton, Tooltip, Button } from '@mui/material';
import React, { ReactEventHandler } from 'react';

import { activityFormattedDate, formattedDate, timeSince } from '../../helper';
import WsColors from '../../WsColors';
import Label from '../Label/Label';
import DateOutline from '../WsIcon/DateOutline';
import JobOutline from '../WsIcon/JobOutline';
import WsIconProfile from '../WsIcon/Profile';
import Time from '../WsIcon/Time';
import { WsIconText } from '../WsIconText';
import { WsTypography, WsTypoColors, WsTypoVariants } from '../WsTypography';

export interface WsTaskCardProps {
  title: string;
  description?: string;
  date: string;
  taskType?: string;
  jobRole?: string;
  jobCode?: string;
  createdBy: string;
  createdByImgUrl?: string;
  activeUsers?: string[];
  checkBoxOnClick?: ReactEventHandler;
  checkBoxNeeded?: boolean;
  onSnoozeClick?: () => void;
  onCompleteTask?: () => void;
  onCandidateNameClick?: () => Promise<void> | void;
  onJobTitleClick?: (jobId: string) => void;
  jobId?: string;
  taskDueDate?: Date | string;
  taskStatus: string;
  taskDescription: string;
  onEditClick?: () => void;
}
export interface AvatarProps {
  color?: string;
  bgColor: string;
}

const WsTaskCard = ({
  title = '',
  description = '',
  date = '',
  jobRole = '',
  jobCode = '',
  createdBy,
  checkBoxNeeded = true,
  onSnoozeClick,
  onCandidateNameClick,
  taskDueDate,
  taskStatus,
  jobId,
  onJobTitleClick,
  onCompleteTask,
  taskDescription,
  onEditClick,
}: WsTaskCardProps) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
    <Stack direction="row">
      <Grid container item xs={6} onClick={onCandidateNameClick} sx={{ cursor: 'pointer' }}>
        <Label>{title}</Label>
      </Grid>
      {taskStatus != 'CLOSED' && (
        <Grid item xs={6} container justifyContent="flex-end">
          <Stack direction="row">
            <Tooltip title="Edit" placement="top">
              <IconButton onClick={() => onEditClick && onEditClick()}>
                <EditOutlinedIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Snooze" placement="top">
              <IconButton onClick={() => onSnoozeClick && onSnoozeClick()}>
                <SnoozeOutlinedIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Complete" placement="top">
              <IconButton onClick={() => onCompleteTask && onCompleteTask()}>
                <DoneIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      )}
    </Stack>
    <Box ml={checkBoxNeeded ? 4 : 0}>
      {description && (
        <Grid container item xs={12} sm={5}>
          <WsTypography
            color={WsTypoColors.BLACK}
            variant={WsTypoVariants.BODY2}
            onClick={onCandidateNameClick}
            cursor="pointer"
          >
            {description}
          </WsTypography>
        </Grid>
      )}
      {taskDescription && (
        <Grid container item xs={12} mt={description ? 1 : 0}>
          <WsTypography
            color={WsTypoColors.BLACK}
            variant={WsTypoVariants.BODY2}
            onClick={onCandidateNameClick}
            cursor="pointer"
          >
            {<b>Description : </b>}
            {`${taskDescription}`}
          </WsTypography>
        </Grid>
      )}
      <Grid item xs={12} container spacing={1} mt={description || taskDescription ? 1 : 0}>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={6}>
            <Tooltip title="Created By">
              <Button variant="text">
                <WsIconText
                  icon={<WsIconProfile color={WsColors.TertiaryShades700} />}
                  text={`${createdBy}`}
                  textCursor="pointer"
                />
              </Button>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sx={{ cursor: 'pointer' }} direction="row">
            {jobId && jobRole && (
              <WsIconText
                icon={<JobOutline />}
                text={jobRole}
                textCursor="pointer"
                onClick={() => onJobTitleClick && onJobTitleClick(jobId ?? '')}
              />
            )}
            {jobCode && <WsIconText text={jobCode} icon={<></>} />}
          </Grid>
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={6}>
            <Tooltip title={formattedDate(date)}>
              <Button variant="text">
                <WsIconText
                  icon={<DateOutline color={WsColors.TertiaryShades600} />}
                  text={activityFormattedDate(date) ?? 'Not Available'}
                />
              </Button>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            {taskStatus == 'CLOSED' ? (
              <WsIconText
                icon={<Time color={WsColors.TertiaryShades600} />}
                text={`Completed  ${timeSince(date)}`}
              />
            ) : (
              <WsIconText
                icon={<Time color={WsColors.TertiaryShades600} />}
                text={
                  `${timeSince(date)}`.endsWith('ago')
                    ? `Overdue by: ${timeSince(date)}`
                    : `Due: ${timeSince(date)}`
                }
              />
            )}
          </Grid>
          <Grid item xs={4} justifyContent="flex-end">
            {jobCode && (
              <WsTypography>
                {` ${taskStatus} ${taskDueDate ? formattedDate(taskDueDate.toString()) : ''}`}
              </WsTypography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  </Paper>
);

export default WsTaskCard;
